import { useEffect, useState } from 'react';
import { api, Attempt, Bank, Pack, Preview, Topic } from '../api';
import { Icon } from '../Icon';
import { PreferencesPanel } from '../a11y/preferences';

export function Configure({ bank, pack, initialTopicIds, onStart, onBack }: {
  bank: Bank; pack: Pack; onStart: (a: Attempt) => void; onBack: () => void;
  /** Preselected from the study plan; everything stays editable. */
  initialTopicIds?: string[];
}) {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selected, setSelected] = useState<string[]>(initialTopicIds ?? []);
  const [mode, setMode] = useState<'study' | 'timed_mock'>('study');
  const [count, setCount] = useState(10);
  const [minutes, setMinutes] = useState(15);
  const [presetId, setPresetId] = useState<string | null>(null);
  const [assessmentType, setAssessmentType] = useState<'mock' | 'adhoc'>('adhoc');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.get<Topic[]>(`/banks/${bank.bankVersionId}/topics`).then(setTopics); },
    [bank.bankVersionId]);

  // A mock preset is one that carries a real exam's structure: a fixed length,
  // a fixed clock and a blueprint mix. Anything else is a starting point the
  // learner is free to edit, which is what ad-hoc means here.
  const mockPreset = pack.presets.find((p) => p.sectionWeighting?.sections?.length) ?? null;
  const mockSections = mockPreset?.sectionWeighting?.sections ?? [];
  const mockMinutes = mockPreset?.durationSeconds ? Math.round(mockPreset.durationSeconds / 60) : 0;

  /** Switching type is what sets the session up; nothing is left half-applied. */
  function chooseType(next: 'mock' | 'adhoc') {
    setAssessmentType(next);
    if (next === 'mock' && mockPreset) {
      setPresetId(mockPreset.id);
      setCount(mockPreset.itemCount ?? 180);
      setMinutes(mockMinutes);
      setMode('timed_mock');
      // The blueprint decides the mix, so a hand-picked topic filter would
      // quietly contradict it.
      setSelected([]);
    } else {
      setPresetId(null);
    }
  }

  // The eligible count is recomputed on EVERY settings change (FR-028), so the
  // learner never discovers a shortfall after pressing start.
  useEffect(() => {
    let cancelled = false;
    api.post<Preview>('/attempts/preview', {
      bankVersionId: bank.bankVersionId,
      topicIds: selected.length ? selected : undefined,
      requestedCount: count,
    }).then((p) => { if (!cancelled) setPreview(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [bank.bankVersionId, selected, count]);

  const available = preview?.eligibleUniqueCount ?? 0;
  const tooMany = count > available;

  /** Applying a preset fills the form; every field stays editable afterwards. */
  function applyPreset(id: string) {
    const p = pack.presets.find((x) => x.id === id);
    setPresetId(id);
    if (!p) { setSelected([]); return; }
    if (p.itemCount) setCount(p.itemCount);
    if (p.durationSeconds) setMinutes(Math.round(p.durationSeconds / 60));
    setMode(p.modes.includes('timed_mock') && p.durationSeconds ? 'timed_mock' : 'study');
    setSelected(p.topicFilter ?? []);
  }

  async function start() {
    setStarting(true); setError(null);
    try {
      const attempt = await api.post<Attempt>('/attempts', {
        bankVersionId: bank.bankVersionId, mode, requestedCount: count,
        durationSeconds: mode === 'timed_mock' ? minutes * 60 : null,
        topicIds: selected.length ? selected : undefined,
        presetId: presetId ?? undefined,
      });
      onStart(attempt);
    } catch (e: any) { setError(e.message); setStarting(false); }
  }

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back to banks</button>
      <h1>Configure your session</h1>
      <p className="muted">{bank.subject} · {bank.edition} · {bank.itemCount} questions available</p>

      {/* The type comes first because it decides everything below it: a mock is
          a fixed format with nothing to choose, so the remaining sections only
          appear for an ad-hoc session. Length comes last because its live
          "N questions match" count depends on the mode and topics above it. */}
      <fieldset>
        <legend>Assessment type</legend>

        {mockPreset && (
          <label className="choice" style={{ marginBottom: '0.75rem' }}>
            <input type="radio" name="assessmentType" value="mock"
                   checked={assessmentType === 'mock'}
                   onChange={() => chooseType('mock')} />
            <span>
              <strong>Mock PTA exam</strong>
              <br />
              <span className="muted">
                {mockPreset.itemCount} questions in {mockSections.length} timed sections of{' '}
                {mockSections[0]?.questionCount}, {mockMinutes} minutes in total. The subject mix
                follows the published entry-level PTA blueprint. Length, timing and mix are fixed.
              </span>
            </span>
          </label>
        )}

        <label className="choice">
          <input type="radio" name="assessmentType" value="adhoc"
                 checked={assessmentType === 'adhoc'}
                 onChange={() => chooseType('adhoc')} />
          <span>
            <strong>Ad-hoc</strong>
            <br />
            <span className="muted">
              Choose your own number of questions and time limit, and narrow to the topics you
              want to work on.
            </span>
          </span>
        </label>

        {assessmentType === 'mock' && (
          <div className="notice" role="status" style={{ marginTop: '1rem' }}>
            <strong>Fixed format.</strong> {mockSections.length} sections of{' '}
            {mockSections[0]?.questionCount} questions,{' '}
            {Math.round((mockSections[0]?.durationSeconds ?? 0) / 60)} minutes each. A section
            cannot be reopened once you end it or its time runs out, so the questions you leave
            behind stay behind — the same as the real thing.
          </div>
        )}
      </fieldset>

      {assessmentType === 'adhoc' && pack.presets.filter((p) => p.id !== mockPreset?.id).length > 0 && (
        <fieldset>
          <legend>Start from a preset</legend>
          <p className="muted">
            Presets mirror the real test's section lengths and timing. Everything below stays
            editable after you pick one.
          </p>
          <label htmlFor="preset" className="visually-hidden">Preset</label>
          <select id="preset" value={presetId ?? ''} onChange={(e) => applyPreset(e.target.value)}>
            <option value="">Custom session</option>
            {pack.presets.filter((p) => p.id !== mockPreset?.id).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.itemCount ? ` — ${p.itemCount} questions` : ''}
                {p.durationSeconds ? `, ${Math.round(p.durationSeconds / 60)} min` : ', untimed'}
              </option>
            ))}
          </select>
        </fieldset>
      )}

      {assessmentType === 'adhoc' && (
      <fieldset>
        <legend>Mode</legend>
        {(['study', 'timed_mock'] as const).map((m) => (
          <label key={m} className="choice" style={{ marginBottom: '0.5rem' }}>
            <input type="radio" name="mode" value={m} checked={mode === m} onChange={() => setMode(m)} />
            <span>
              <strong>{m === 'study' ? 'Study' : 'Timed mock'}</strong>
              <br />
              <span className="muted">
                {m === 'study'
                  ? 'Explanations appear as you answer. Untimed.'
                  : 'Explanations are withheld until you submit. The clock runs on the server.'}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
      )}

      {assessmentType === 'adhoc' && (
      <fieldset>
        <legend>Topics</legend>
        <p className="muted">Leave all unticked to draw from the whole bank.</p>
        {topics.map((t) => (
          <label key={t.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <input type="checkbox" checked={selected.includes(t.id)}
                   onChange={(e) => setSelected((cur) =>
                     e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id))} />
            <span>{t.name} <span className="muted">({t.itemCount})</span></span>
          </label>
        ))}
      </fieldset>
      )}

      {assessmentType === 'adhoc' && (
      <fieldset>
        <legend>Length</legend>
        <label htmlFor="count">Number of questions</label>
        <input id="count" type="number" min={1} max={Math.max(available, 1)} value={count}
               aria-describedby="count-help"
               onChange={(e) => setCount(Math.max(1, Number(e.target.value)))} />
        <p id="count-help" className="muted" style={{ marginTop: '0.5rem' }} aria-live="polite">
          {available} question{available === 1 ? '' : 's'} match these settings.
          {preview?.unratedCount ? ` ${preview.unratedCount} have unrated difficulty.` : ''}
        </p>

        {mode === 'timed_mock' && (
          <>
            <label htmlFor="minutes" style={{ marginTop: '1rem' }}>Time limit (minutes)</label>
            <input id="minutes" type="number" min={1} max={480} value={minutes}
                   onChange={(e) => setMinutes(Math.max(1, Number(e.target.value)))} />
          </>
        )}
      </fieldset>
      )}

      {tooMany && (
        <div className="notice notice--warn" role="status">
          <strong>Not enough questions.</strong> You asked for {count} but only {available} match
          these settings. Questions are never repeated within a session — reduce the count or
          select more topics.
        </div>
      )}

      <details className="card card--plain">
        <summary style={{ cursor: 'pointer', minHeight: '2.75rem', display: 'flex', alignItems: 'center' }}>
          Reading and display options
        </summary>
        <div style={{ marginTop: '1rem' }}><PreferencesPanel /></div>
      </details>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Before you start</h2>
        <ul>
          {assessmentType === 'mock' ? (
            <li>
              {count} questions in {mockSections.length} sections of {mockSections[0]?.questionCount},{' '}
              {Math.round((mockSections[0]?.durationSeconds ?? 0) / 60)} minutes per section
              ({mockMinutes} minutes in total).
            </li>
          ) : (
            <li>{count} question{count === 1 ? '' : 's'}, {mode === 'timed_mock' ? `${minutes} minutes` : 'untimed'}.</li>
          )}
          {assessmentType === 'mock' && (
            <li>
              Each section is timed separately. <strong>Ending a section is final</strong> — you
              cannot return to its questions.
            </li>
          )}
          {(assessmentType === 'mock'
            || pack.presets.find((p) => p.id === presetId)?.simulationLabelRequired) && (
            <li>
              <strong>This is a practice simulation.</strong> It mirrors the real test's length and
              timing, but it is not an official test and produces no official score.
            </li>
          )}
          <li>{mode === 'study' ? 'Explanations appear after each answer.' : 'Explanations appear after you submit.'}</li>
          {pack.presets.find((p) => p.id === presetId)?.answersRevisable ? (
            <li>
              <strong>Answers lock when a section ends.</strong> Until then you can change any answer
              and flag questions to come back to; a review screen before each section ends lists the
              flagged and unanswered ones. You will not see whether an answer is correct until you finish.
            </li>
          ) : (
            <li><strong>Answers are final.</strong> Once you answer a question you cannot change it, so read each one carefully before choosing.</li>
          )}
          <li>Answers save as you go and survive a reload. You can still flag questions and move between them freely.</li>
          <li>You will receive a raw practice score — correct answers out of questions scored.
              It is not a scaled score and does not predict any examination outcome.</li>
        </ul>
        {error && <div className="notice notice--warn" role="alert">{error}</div>}
        <button className="btn-primary" onClick={start} disabled={tooMany || starting || available === 0}>
          {starting ? 'Starting…' : <><Icon name="play" />Start session</>}
        </button>
      </div>
    </>
  );
}
